import CategoryItem from "@modules/program/CategoryItem";
import ProgramPageHeader from "@modules/program/Header";
import ProgramPageMobileNavigator from "@modules/program/MobileNavigator";
import ProgramPageSidebar from "@modules/program/Sidebar";
import useProgramConds from "@templates/program/hooks/useProgramConds";
import { IconButton } from "@shared/components/icon-button";
import { ICON_SIZE } from "@shared/components/icon-button/types";
import IconBackArrow from "@svg/header/back-arrow";
import { IProgramInput, ProgramProduct, ProgramSubCategory } from "@types";
import { FC, useMemo, useRef, useState } from "react";
import { programSubscriptionBannerAfterSubCategoryIndex as subscriptionBannerIndex } from "./program/constants";
import { useIsMobile } from "@shared/hooks/useIsMobile";
import Breadcrumb from "@shared/components/breadcrumb";

interface Props {
  data: IProgramInput;
}

export const Program: FC<Props> = ({ data }) => {
  const isLessThan768 = useIsMobile(768);
  const topRef = useRef<HTMLDivElement | null>(null);
  const categories = data?.data?.categories || [];

  const { activeIndex, offsetTop, scrollMargin, backHandler, navOffsetTop } =
    useProgramConds(categories ?? []);

  const [clickedCategory, setClickedCategory] = useState<number | null>(null);

  const categoryClickHandler = (catId: number) => setClickedCategory(catId);

  const showingBannerProductId = useMemo(() => {
    const allSubCategories = categories
      .map((category) => category?.subCategories ?? null)
      .filter(Boolean) as ProgramSubCategory[][];

    const flatSubcategories = allSubCategories.flat(1);

    const allItemsArray = flatSubcategories
      .map((subcategory) => subcategory.products)
      .filter(Boolean) as ProgramProduct[][];

    const allItemsFlatArray = allItemsArray.flat(1);

    const productsCount = allItemsFlatArray.length;

    const showingBannerIndex =
      (productsCount > subscriptionBannerIndex
        ? subscriptionBannerIndex
        : productsCount) - 1;

    return allItemsFlatArray[showingBannerIndex].id;
  }, [categories]);

  return (
    <div className="relative">
      <nav
        className="flex items-center justify-start gap-1.5  border-b-2 border-fds-divider min-h-fds-52 h-fds-52 mb-0.5 px-2  sticky z-30 bg-fds-primary md:hidden"
        style={{ top: navOffsetTop }}
      >
        <IconButton
          icon={<IconBackArrow />}
          size={ICON_SIZE.MEDIUM}
          className="min-w-10"
          iconClassName="text-fds-icon-secondary"
          onClick={backHandler}
          aria-label="بازگشت"
        />
        <p className="title-lg truncate">
          {data.data.name} {data.data.name} {data.data.name}
        </p>
      </nav>
      <div className="w-full ">
        <Breadcrumb
          customLabel={data?.data.name || ""}
          itemsClassName="truncate"
        />
      </div>
      <ProgramPageHeader data={data} />
      <div
        ref={topRef}
        className="section-container lg:px-12 w-full mx-auto pt-8   lg:pt-12"
      >
        <div className="w-full flex flex-col lg:flex-row lg:gap-6">
          <ProgramPageMobileNavigator
            {...{
              activeIndex,
              categories,
              offsetTop,
              clickHandler: categoryClickHandler,
            }}
          />
          <div className="hidden lg:block w-full lg:w-1/3 bg-fds-primary z-20 py-2  lg:py-0 sticky">
            <ProgramPageSidebar
              categories={categories}
              activeIndex={activeIndex || 0}
              clickHandler={categoryClickHandler}
            />
          </div>
          <section className="w-full lg:w-2/3 flex flex-col gap-2.5 relative">
            {categories.map((category, i) => {
              return (
                <CategoryItem
                  scrollMargin={i === 0 ? scrollMargin + 52 : scrollMargin}
                  key={category.id}
                  category={category}
                  isFirst={i === 0}
                  offsetTop={isLessThan768 ? offsetTop + 52 : offsetTop}
                  clickedCategory={clickedCategory}
                  resetClickedCategory={() => setClickedCategory(null)}
                  showingBannerProductId={showingBannerProductId}
                />
              );
            })}
          </section>
        </div>
      </div>
    </div>
  );
};
